import React, { useCallback, useEffect, useState } from 'react';
import { useTheme } from '@mui/material';
import { Accordion, useModal } from '@europrocurement/flexy-components';
import {
    CATEGORY_BODACC,
    useDossierValidatorContext,
    useSelectedDossierContext,
    VALIDATOR_TYPE_BODACC,
    VALIDATOR_TYPE_LEGAL_ANNOUCEMENT,
} from '@europrocurement/l2d-modules';
import { useHandleError } from '@europrocurement/l2d-hooks';
import { useSnackbar } from 'notistack';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { LegalAnnoucement } from '@europrocurement/l2d-modules/modules/Dossiers/types';
import { DossierOpenSearch } from '@europrocurement/l2d-domain';
import {
    AddArticleParamsType,
    ArticleBodaccValidationProcessStatus,
    ArticleValidationProcessStatus,
    DossierArticle,
    UpdateArticleParamsType,
    useDossierArticle,
    useDossierLegalAnnoucements,
    useDossierProcesses,
    useDossierProcessesActionsAndRender,
} from '../../hooks';
import DossierArticlesProcessesList from '../DossierArticlesProcessesList/DossierArticlesProcessesList';
import AddBodaccForm, { AddBodaccFormData } from '../AddBodaccForm/AddBodaccForm';
import { DossierArticleProcessMenuItemProps } from '../DossierArticleProcessMenu/DossierArticleProcessMenu';
import DossierProcessesFooter from '../DossierProcessesFooter/DossierProcessesFooter';
import UpdateValidatorForm from '../UpdateValidatorForm/UpdateValidatorForm';
import { DossierProcessesArticlesProps } from '../DossierProcesses/DossierProcessesType';
import EditBodaccForm, { EditBodaccFormFormData } from '../EditBodaccForm/EditBodaccForm';
import SelectLegalAnnoucementForm from '../SelectLegalAnnoucementForm/SelectLegalAnnoucementForm';

const DossierBodaccProcesses: React.FunctionComponent<DossierProcessesArticlesProps> = function ({
    articles,
}) {
    const articleCategoryId = CATEGORY_BODACC;
    const validatorTypeId = VALIDATOR_TYPE_BODACC;
    const { modalActions } = useModal();
    const { palette } = useTheme();
    const { addArticle, updateArticle } = useDossierArticle();
    const { dossier, refreshDossier } = useSelectedDossierContext();
    const { apiErrorMessageSnackbar } = useHandleError();
    const { enqueueSnackbar } = useSnackbar();
    const { validators } = useDossierValidatorContext();
    const {
        createValidatorProcess,
        updateValidatorProcess,
        getProcessesByArticle,
        findProcessByArticle,
        canCreateValidatorProcess,
        getArticleProcessesStatusLabel,
        getArticleProcessesStatusColor,
    } = useDossierProcesses();

    const {
        handleDeleteArticle,
        handlePublishLegalAnnoucement,
        loadLegalAnnoucements,
        actionUpdateValidatorStatus,
        actionRemoveLinkedLegalAnouncement,
        actionShowLegalAnouncement,
        actionPublishLegalAnouncement,
        actionDeleteArticlePresta,
        actionAttachLegalAnouncement,
        getLegalAnnoucementByArticle,
    } = useDossierProcessesActionsAndRender();

    const { deleteLegalAnnoucement, getPrestaLegalAnouncement } = useDossierLegalAnnoucements();

    const [status, setStatus] = useState<ArticleValidationProcessStatus>('to-link');
    const [processing, setProcessing] = useState<boolean>(false);
    const [legalAnnoucements, setLegalAnnoucements] = useState<Array<LegalAnnoucement>>([]);
    const [legalAnnoucementsLoaded, setLegalAnnoucementsLoaded] = useState<boolean>(true);
    const [canCreateProcess, setCanCreateProcess] = useState<boolean>(false);

    const getArticleBodaccProcessesStatus = (
        pDossier: DossierOpenSearch,
        pValidatorTypeId: number,
        articleIds: Array<number>,
    ): ArticleValidationProcessStatus => {
        const statuses = articleIds.map((articleId) => {
            const processes = getProcessesByArticle(pDossier, pValidatorTypeId, articleId);
            if (processes.length > 0) {
                return processes[0].statusCode as ArticleBodaccValidationProcessStatus;
            }
            return 'pending';
        });

        if (statuses.length === 0) {
            return 'pending';
        }

        // Bloqué (priorité 1)  3 = avis publié KO ,4 = rejet temporaire, 5 = annulé (double emploi)
        const isLocked = statuses.some((element) => ['3', '4', '5'].includes(element));
        if (isLocked) {
            return 'failed';
        }

        // À faire (priorité 2)
        const unSubmitted = statuses.some((element) => element === '0');
        if (unSubmitted) {
            return 'pending';
        }

        // En cours (priorité 3)
        const onGoing = statuses.some((element) => element === '1');
        if (onGoing) {
            return 'ongoing';
        }

        // Terminé (priorité 4) 2 = publié OK, 6 = soldé sans avis publié réussi
        const done = statuses.every((element) => ['2', '6'].includes(element));
        if (done) {
            return 'done';
        }

        return 'pending';
    };

    const handleAttachBodacc = async (idLegalAnnoucement: number, articleId: number) => {
        setProcessing(true);
        try {
            const process = findProcessByArticle(dossier, [validatorTypeId], articleId);
            if (!process) {
                throw new Error("Aucun processus de validation en cours sur l'article");
            }

            // Get Legal Anoucement status
            let statusPrestaBodacc = '0';
            // fetch presta LegalAnoucement to get current status of the bodacc if available
            const prestaLegalAnoucement = await getPrestaLegalAnouncement(
                idLegalAnnoucement.toString(),
            );

            if (
                prestaLegalAnoucement.bodacc &&
                prestaLegalAnoucement.bodacc.bdcFSoumisGtc &&
                prestaLegalAnoucement.bodacc.bdcStatutTraitementGtc
            ) {
                statusPrestaBodacc = prestaLegalAnoucement.bodacc.bdcStatutTraitementGtc.toString();
            } else {
                enqueueSnackbar(
                    "La récupération du statut du bodacc n'est pas réalisable. Statu à définir manuellement",
                    {
                        variant: 'error',
                    },
                );
            }

            // update validator process, attach idLegalAnoucement and set status
            await updateValidatorProcess(
                process.id as number,
                statusPrestaBodacc,
                idLegalAnnoucement as unknown as string,
            );

            // Used to reload legalAnoucments linked to a dossier
            setLegalAnnoucementsLoaded(false);
            refreshDossier();
        } catch (err) {
            console.error(err);
            enqueueSnackbar('Une erreur est survenue durant le rattachement du bodacc', {
                variant: 'error',
            });
        } finally {
            modalActions.close();
            setProcessing(false);
        }
    };

    const onSubmitAddBodacc = async (data: AddBodaccFormData) => {
        setProcessing(true);

        try {
            const articleId = await addArticle({
                prestationId: data.prestationId,
                label: data.title,
                factArticleId: data.articleId,
            } as AddArticleParamsType);
            // Create a validator process with a to-link status
            await createValidatorProcess(dossier.id as number, [articleId]);
            modalActions.close();
            refreshDossier();
        } catch (err) {
            apiErrorMessageSnackbar(
                err as Error,
                "Une erreur est survenue lors de l'ajout du bodacc",
            );
        } finally {
            setProcessing(false);
        }
    };

    const onSubmitEditBodacc = async (data: EditBodaccFormFormData) => {
        setProcessing(true);

        try {
            await updateArticle({
                prestaArticleId: data.prestaArticleId,
                prestationId: data.prestationId,
                label: data.title,
                factArticleId: data.articleId,
            } as UpdateArticleParamsType);
            modalActions.close();
            refreshDossier();
        } catch (err) {
            apiErrorMessageSnackbar(
                err as Error,
                'Une erreur est survenue lors de la modification du bodacc',
            );
        } finally {
            setProcessing(false);
        }
    };

    const handleAddBodacc = useCallback(() => {
        modalActions.call(
            <AddBodaccForm
                onCancel={() => {
                    setProcessing(false);
                    modalActions.close();
                }}
                onSubmit={onSubmitAddBodacc}
                dossier={dossier}
            />,
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dossier, modalActions, processing]);

    const handleEditBodacc = useCallback(
        (prestaArticle: DossierArticle) => {
            modalActions.call(
                <EditBodaccForm
                    onCancel={() => {
                        setProcessing(false);
                        modalActions.close();
                    }}
                    prestaArticle={prestaArticle}
                    onSubmit={onSubmitEditBodacc}
                    dossier={dossier}
                />,
            );
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dossier, modalActions, processing],
    );

    useEffect(() => {
        if (!dossier.id) {
            return;
        }

        if (!legalAnnoucementsLoaded) {
            setLegalAnnoucementsLoaded(true);

            loadLegalAnnoucements(dossier, articleCategoryId, [
                VALIDATOR_TYPE_BODACC,
                VALIDATOR_TYPE_LEGAL_ANNOUCEMENT,
            ])
                .then((items) => {
                    setLegalAnnoucements(items);
                })
                .catch(() => {
                    enqueueSnackbar(
                        'Une erreur est survenue lors de la récupération des annonces légales liées',
                        {
                            variant: 'error',
                        },
                    );
                    setLegalAnnoucements([]);
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [legalAnnoucementsLoaded]);

    useEffect(() => {
        setLegalAnnoucementsLoaded(false);
        setCanCreateProcess(canCreateValidatorProcess(dossier, validatorTypeId));

        setStatus(
            getArticleBodaccProcessesStatus(
                dossier,
                validatorTypeId,
                articles.map((prestaArticle) => prestaArticle.id),
            ),
        );

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [validators, dossier, validatorTypeId, articles]);

    useEffect(() => {
        setLegalAnnoucementsLoaded(false);
    }, [dossier.id, validatorTypeId, articles]);

    const handleDeleteLegalAnnoucement = async (article: DossierArticle) => {
        await deleteLegalAnnoucement(
            article,
            dossier,
            validatorTypeId,
            'Le rattachement a bien été supprimé',
            'Une erreur est survenue durant la suppression du rattachement',
            'to-link',
        );

        refreshDossier();
        setLegalAnnoucementsLoaded(false);
        modalActions.close();
    };

    const getItemActions = (item: DossierArticle) => {
        let actions: Array<DossierArticleProcessMenuItemProps> = [];
        let statusProcess: ArticleValidationProcessStatus = 'to-link';
        const processes = getProcessesByArticle(dossier, validatorTypeId, item.id);
        if (processes.length > 0) {
            statusProcess = processes[0].statusCode as ArticleValidationProcessStatus;
        }
        const legalAnouncementLinked = getLegalAnnoucementByArticle(
            item.id,
            dossier,
            validatorTypeId,
            legalAnnoucements,
        );

        const hasLegalAnnoucement = !!legalAnouncementLinked;

        if (['to-link'].includes(statusProcess) && canCreateProcess) {
            actions = [
                ...actions,
                actionAttachLegalAnouncement(() =>
                    modalActions.call(
                        <SelectLegalAnnoucementForm
                            processing={processing}
                            article={item}
                            onItemSelected={handleAttachBodacc}
                            filters={{
                                bodacc: true,
                                validationProcess: false,
                            }}
                            title="Rechercher un bodacc"
                            placeholder="Rechercher une annonce légale avec bodacc"
                        />,
                    ),
                ),
                ...(canCreateProcess && !hasLegalAnnoucement
                    ? [
                          actionUpdateValidatorStatus(() => {
                              const process = findProcessByArticle(
                                  dossier,
                                  [validatorTypeId],
                                  item.id,
                              );
                              let currentStatus = { value: '', label: '' };
                              if (process) {
                                  currentStatus = {
                                      value: process.statusCode,
                                      label: process.statusLabel,
                                  };
                              }

                              modalActions.call(
                                  <UpdateValidatorForm
                                      validatorTypeId={validatorTypeId}
                                      processing={processing}
                                      article={item}
                                      dossier={dossier}
                                      onItemSelected={() => {
                                          modalActions.close();
                                          refreshDossier();
                                      }}
                                      currentStatus={currentStatus}
                                  />,
                              );
                          }),
                          {
                              label: 'Modifier la démarche',
                              icon: 'edit' as IconName,
                              onClick: () => handleEditBodacc(item),
                          },
                      ]
                    : []),

                actionDeleteArticlePresta(
                    () => handleDeleteArticle(item.id.toString(), setProcessing, refreshDossier),
                    () => modalActions.close(),
                ),
            ];
        } else if (['0', '1', '2', '3', '4', '5', '6'].includes(statusProcess)) {
            actions = [
                ...actions,
                ...(hasLegalAnnoucement
                    ? [
                          actionShowLegalAnouncement(
                              item.id,
                              dossier,
                              validatorTypeId,
                              legalAnnoucements,
                          ),
                          actionPublishLegalAnouncement(
                              () =>
                                  handlePublishLegalAnnoucement(
                                      item,
                                      dossier,
                                      validatorTypeId,
                                      () => {
                                          setLegalAnnoucementsLoaded(false);
                                          refreshDossier();
                                      },
                                      () => modalActions.close(),
                                  ),
                              () => modalActions.close(),
                          ),
                          actionRemoveLinkedLegalAnouncement(handleDeleteLegalAnnoucement, () =>
                              modalActions.close(),
                          ),
                      ]
                    : []),
                ...(canCreateProcess && !hasLegalAnnoucement
                    ? [
                          actionAttachLegalAnouncement(() =>
                              modalActions.call(
                                  <SelectLegalAnnoucementForm
                                      processing={processing}
                                      article={item}
                                      onItemSelected={handleAttachBodacc}
                                      filters={{
                                          bodacc: true,
                                          validationProcess: false,
                                      }}
                                      title="Rechercher un bodacc"
                                      placeholder="Rechercher une annonce légale avec bodacc"
                                  />,
                              ),
                          ),
                          actionUpdateValidatorStatus(() => {
                              const process = findProcessByArticle(
                                  dossier,
                                  [validatorTypeId],
                                  item.id,
                              );
                              let currentStatus = { value: '', label: '' };
                              if (process) {
                                  currentStatus = {
                                      value: process.statusCode,
                                      label: process.statusLabel,
                                  };
                              }
                              modalActions.call(
                                  <UpdateValidatorForm
                                      validatorTypeId={validatorTypeId}
                                      processing={processing}
                                      article={item}
                                      dossier={dossier}
                                      onItemSelected={() => {
                                          modalActions.close();
                                          refreshDossier();
                                      }}
                                      currentStatus={currentStatus}
                                  />,
                              );
                          }),
                      ]
                    : []),
            ];
        }
        return actions;
    };

    return (
        <Accordion
            title="Bodacc"
            expandLabel={getArticleProcessesStatusLabel(status)}
            expanded
            expandColor={getArticleProcessesStatusColor(status)}
            sx={{ backgroundColor: palette.background.paper }}
            contentSxProps={{ gap: '16px', display: 'grid' }}
        >
            <DossierArticlesProcessesList
                articles={articles}
                dossier={dossier}
                validatorTypeId={validatorTypeId}
                legalAnnoucements={legalAnnoucements}
                getItemActions={getItemActions}
            />

            {canCreateProcess && (
                <DossierProcessesFooter
                    handleAddItem={handleAddBodacc}
                    // handleMainAction={handlePublishBodacc}
                    addItemTextBtn="Ajouter un bodacc"
                    // mainActionTextBtn="Publier"
                    canAddItem
                    canHandleMainAction
                />
            )}
        </Accordion>
    );
};

export default DossierBodaccProcesses;
